import { normalizeRoomCode, PRIVATE_MATCH_TTL_SECONDS, roomCodeSchema } from '@drawgon/shared';
import { Link, useParams } from 'react-router-dom';
import { heading, lead, panel, sectionTitle } from '../lib/ui';

/**
 * プライベートマッチの招待リンクの着地点（`/m/:code`、#153 / #217）。
 *
 * **アプリが入っていれば、ここは開かない。** Universal Links（`/.well-known/apple-app-site-association`）で
 * アプリが直接開き、参加へ進む。ここに来るのは、アプリが入っていない端末か、Universal Links が効かなかったとき
 * （アプリ内のブラウザ・長押しで「Safari で開く」を選んだ など）。
 *
 * **ルームの中身は読まない**（相手の名前も選手も出さない。コードは URL に書いてあるものを出すだけ）。
 * 公開サイトは認証が無いので、ルームを引くとコードの総当たりの窓口になる。
 */

/**
 * App Store の URL。**まだ公開していないので null**（#41 でタイトルとストアが決まったら入れる）。
 * null の間は「じゅんびちゅう」と出す。1 か所の定数にしておき、差し替えをここだけで済ませる。
 */
const APP_STORE_URL: string | null = null;

const TTL_MINUTES = PRIVATE_MATCH_TTL_SECONDS / 60;

export function MatchInvite() {
  const { code: raw = '' } = useParams();
  const code = normalizeRoomCode(raw);

  if (!roomCodeSchema.safeParse(code).success) {
    return (
      <div className="pt-8">
        <h1 className={heading}>招待リンクが まちがっています</h1>
        <p className={`${lead} mt-3`}>
          もらったリンクを もういちど たしかめてね。コードを 教えてもらえば、アプリで 入力して
          参加できます。
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-6 pt-8">
      <h1 className={heading}>ともだちと たいせんしよう</h1>
      <p className={lead}>
        ルームに さそわれました。drawgon の アプリで このリンクを ひらくと、そのまま 参加できます。
      </p>

      <section className={panel}>
        <h2 className={sectionTitle}>ルームコード</h2>
        {/* 声に出して伝えられるよう、1 文字ずつ読ませる（アプリのロビーと同じ）。 */}
        <p
          className="mt-2 font-display text-4xl tracking-[0.3em]"
          aria-label={code.split('').join(' ')}
        >
          {code}
        </p>
        <p className="mt-3 text-sm text-text-muted">
          アプリが ひらかないときは、アプリの「ともだちと たいせん」→「ルームに 参加する」で
          このコードを 入れてね。ルームは つくってから {TTL_MINUTES} 分で しまります。
        </p>
      </section>

      <section className={panel}>
        <h2 className={sectionTitle}>アプリを もっていないとき</h2>
        {APP_STORE_URL ? (
          <p className="mt-2">
            <a href={APP_STORE_URL} className="text-text underline decoration-2 underline-offset-4">
              App Store で drawgon を ひらく
            </a>
          </p>
        ) : (
          <p className="mt-2 text-sm text-text-muted">drawgon の アプリは じゅんびちゅうです。</p>
        )}
      </section>

      <p className="text-sm text-text-muted">
        <Link to="/" className="text-text underline decoration-2 underline-offset-4">
          drawgon について
        </Link>
      </p>
    </div>
  );
}
